import { toXmaxSdkError, XmaxSdkError } from '../../shared/sdk-error'

const HEIC_EXTENSION_RE = /\.(?:heic|heif)$/i
const HEIC_MIME_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
])

const HEIF_BRANDS = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'hevm',
  'hevs',
  'mif1',
  'msf1',
])

function readAscii(bytes: Uint8Array, offset: number, length: number): string {
  let value = ''
  for (let index = offset; index < offset + length && index < bytes.length; index += 1) {
    value += String.fromCharCode(bytes[index])
  }
  return value
}

async function hasHeifFileSignature(file: File): Promise<boolean> {
  const bytes = new Uint8Array(await file.slice(0, 64).arrayBuffer())
  if (bytes.length < 12 || readAscii(bytes, 4, 4) !== 'ftyp') {
    return false
  }

  const brands: string[] = []
  for (let offset = 8; offset + 4 <= bytes.length; offset += 4) {
    brands.push(readAscii(bytes, offset, 4).toLowerCase())
  }

  // AVIF also uses the ISO BMFF container and can advertise mif1. Do not
  // accidentally send it through the HEVC decoder.
  if (brands.includes('avif') || brands.includes('avis')) {
    return false
  }

  return brands.some((brand) => HEIF_BRANDS.has(brand))
}

/** Detect HEIC/HEIF even when a browser supplies an empty or generic MIME type. */
export async function isHeicImageFile(file: File): Promise<boolean> {
  const mime = file.type.toLowerCase()
  if (HEIC_EXTENSION_RE.test(file.name) || HEIC_MIME_TYPES.has(mime)) {
    return true
  }

  return hasHeifFileSignature(file)
}

export type NormalizeHeicImageOptions = {
  /** JPEG quality used for browser-compatible output. Defaults to 0.9. */
  quality?: number
}

/**
 * Convert HEIC/HEIF to JPEG before browser preview, Canvas, RTC, or upload.
 * Every other file is returned unchanged and does not load the WASM decoder.
 */
export async function normalizeHeicImageFile(
  file: File,
  options: NormalizeHeicImageOptions = {},
): Promise<File> {
  try {
    if (!(await isHeicImageFile(file))) {
      return file
    }

    const { heicTo, isHeic } = await import('heic-to/csp')
    if (!(await isHeic(file))) {
      throw new XmaxSdkError('INVALID_INPUT', 'The selected file is not a valid HEIC/HEIF image')
    }

    const blob = await heicTo({
      blob: file,
      type: 'image/jpeg',
      quality: options.quality ?? 0.9,
    })
    const baseName = file.name.replace(/\.(?:heic|heif)$/i, '') || 'image'

    return new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    })
  } catch (error) {
    throw toXmaxSdkError(error, {
      code: 'MEDIA_PROCESSING_ERROR',
      message: 'Failed to decode HEIC/HEIF image',
    })
  }
}
